/**
 * Hangar: building a force to a target.
 *
 * The screen the forces epic is for. A player searches the library by name,
 * adds designs to a list, and watches what it is worth against a target they
 * set — which warns rather than blocks, because a force over its target is
 * still a force, and only an event that enforces one turns it away.
 *
 * What is here and what is not, deliberately:
 *
 * - The library is helm's published index, filtered in the browser. No
 *   round trip per keystroke, and the same file the api reads for reports.
 * - Battle value is each design's own, summed. A crew's effect on it is
 *   helm's rules and belongs on the wasm side, so crews are shown at the
 *   regular 4/5 the published figure assumes and are not editable yet.
 * - Nothing is saved. `blue.lance.force` is the next piece; until it exists
 *   the list lives for as long as the page does, and the screen says so
 *   rather than offering a button that would lose the work.
 */

import { type Session, currentSession } from "../api";
import { footer } from "../chrome";
import {
  chart,
  describe,
  sayDamage,
  sayHexes,
  scaleAcross,
  scaleFor,
  bestHexes,
  totalCurve,
} from "../curve";
import { el, render } from "../dom";
import { library } from "../library";
import { unitIndexUrl, unitSprite } from "../megamek";
import {
  NO_TARGET,
  type Pick,
  type Target,
  shares,
  standing,
  targetLine,
  targetTerms,
  withinTarget,
} from "../force";
import {
  type Picker,
  camoModal,
  camoPicker,
  pickedPixels,
} from "./camo-picker";
import { SPRITE_H, SPRITE_W, applyCamo } from "../camo/mech";
import { paintWhenReady, tile } from "../megamek-art";
import {
  FILTER_KEYS,
  FLAG_KEYS,
  filterKind,
  filterValues,
  loadCurves,
  loadUnits,
  movement,
  parseQuery,
  search,
  stringifyQuery,
  secondName,
  suggestions,
  type Unit,
  excessHeat,
} from "../units";

/** Everything the screen redraws from. */
interface State {
  units: Unit[];
  /**
   * Each design's firepower curve, when the library's prefix carries them.
   * Empty against a prefix written before helm split the index, and every
   * chart on the screen is simply absent - see `loadCurves`.
   */
  curves: Map<Unit, number[]>;
  picks: Pick[];
  target: Target;
  query: string;
  /** How many results are on screen; grows a page at a time. */
  shown: number;
  /** Which of `ORDERS` the results are in. */
  sort: string;
  nextId: number;
  /**
   * The camo the force is painted in, or null for the bare grey a design
   * ships in. The library is drawn bare whatever this holds - see `sprite`.
   */
  camo: ImageData | null;
  /**
   * Bumped whenever the paint changes, so a sprite that finishes decoding
   * after a player has moved on does not land in last camo's colours.
   */
  paint: number;
}

const NUMBER = new Intl.NumberFormat();

/** How many results are drawn before "Show more" is offered. */
const PAGE = 12;

/** The comparisons a number answers to, in the order the box writes them. */
const ORDER_OPS = [":", "=", "<", "<=", ">", ">="];

/**
 * The orders a list of designs can be put in, and which way each one runs.
 *
 * One control rather than two, because every one of these has an end a player
 * means: nobody sorts by battle value to see the cheapest thing in the
 * library first, and nobody sorts by heat to be shown the design that shuts
 * itself down. The direction is part of the choice, so it is named here
 * instead of being a second button that is wrong half the time.
 *
 * `name` is the ordering the search already produces — best match first, and
 * alphabetical within a rank — so choosing it is choosing not to re-sort.
 */
const ORDERS: {
  key: string;
  label: string;
  of?: (unit: Unit) => number | null;
}[] = [
  { key: "name", label: "Best match" },
  { key: "bv", label: "Battle value", of: (unit) => unit.bv },
  { key: "mass", label: "Tonnage", of: (unit) => unit.mass },
  { key: "damage", label: "Peak firepower", of: (unit) => unit.firepower },
  { key: "armour", label: "Armour", of: (unit) => unit.total_armor },
  { key: "range", label: "Max range", of: (unit) => unit.max_range },
  { key: "walk", label: "Walking MP", of: (unit) => unit.walk_mp },
  { key: "jump", label: "Jumping MP", of: (unit) => unit.jump_mp },
  { key: "coolest", label: "Coolest", of: (unit) => excessHeat(unit) },
];

/**
 * The list in one of those orders, biggest first — except the coolest, which
 * is the one figure a player wants the small end of.
 *
 * A design the index has no figure for sorts last either way. It is not a
 * zero, and putting it at the top of "most armour" would be a lie about the
 * only thing the sort claims to know.
 */
function ordered(units: Unit[], key: string): Unit[] {
  const order = ORDERS.find((one) => one.key === key);
  if (!order?.of) return units;
  const { of } = order;
  const up = key === "coolest";
  return [...units].sort((a, b) => {
    const left = of(a);
    const right = of(b);
    if (left === null) return right === null ? 0 : 1;
    if (right === null) return -1;
    return up ? left - right : right - left;
  });
}

/** How much accent each segment keeps, stepping down along the strip. */
const RAMP = [100, 86, 72, 58, 46, 36, 28];

/** One worked example: the query, and what it asks for. */
function example(query: string, means: string): HTMLElement {
  return el("li", {}, [
    el("code", { textContent: query }),
    el("span", { className: "hint", textContent: ` — ${means}` }),
  ]);
}

/**
 * One key in the reference: what it names, what it means, and a term using it.
 *
 * Aliases share a row, because they are the same column and a reader who
 * learns `mass` should not have to meet `tonnage` as news further down.
 */
function filter(keys: string, means: string, eg: string): HTMLElement {
  return el("li", {}, [
    el("code", { className: "hangar-key", textContent: keys }),
    el("span", { className: "hint", textContent: ` ${means}` }),
    el("code", { className: "hangar-key-eg", textContent: eg }),
  ]);
}

/**
 * What each yes-or-no key asks, in one line.
 *
 * Short on purpose. What `melee` counts, and why infantry answers neither
 * way, is helm's reading and takes a paragraph; a player reading this list
 * wants to know which questions can be asked at all.
 *
 * The dialog lists `FLAG_KEYS` rather than this, so a flag added to the query
 * language appears here whether or not anybody wrote it a line — with the
 * fallback below, which says the only thing that is true of every one of them.
 */
const FLAG_MEANINGS: Record<string, string> = {
  melee: "built to fight at arm's length.",
  symmetrical: "mirrored locations hold the same things. Meks only.",
  omni: "weapon pods swap between configurations.",
  clan: "MegaMek counts the design as Clan.",
  biped: "stands on two legs. Meks only.",
  quad: "stands on four legs. Meks only.",
  tripod: "stands on three legs. Meks only.",
};

/** One yes-or-no key: what it asks, without the rules behind the answer. */
function flag(key: string, means = "takes yes or no."): HTMLElement {
  return el("li", {}, [
    el("code", { className: "hangar-key", textContent: key }),
    el("span", { className: "hint", textContent: ` ${means}` }),
    el("code", { className: "hangar-key-eg", textContent: `${key}:yes` }),
  ]);
}

/** One question the library cannot answer yet, and what it would look like. */
function soon(key: string, query: string, means: string): HTMLElement {
  return el("li", {}, [
    el("code", { className: "hangar-soon-term", textContent: query }),
    el("span", { className: "hint", textContent: ` — ${means}` }),
    el("span", { className: "hangar-soon-mark", textContent: "coming soon" }),
  ]);
}

/**
 * A design's picture, at a whole-number scale.
 *
 * A canvas rather than an `<img>`, because the camo is applied here: the
 * compositor is MegaMek's own, ported in `camo/mech.ts`, and it works on
 * pixels. The sprite decode is shared with every other screen through
 * `megamek-art.ts`, so a design drawn in the results and again in the roster
 * is fetched once, and choosing a camo re-composites from that cache rather
 * than going back to the network.
 *
 * `scale` is a whole fraction of the sprite's own size and nothing else. The
 * art is pixel art at 84x72, and 84 forced to 64 is every fourth column
 * dropped and its neighbours smeared across the gap - which costs the
 * silhouette, the one thing a thumbnail is there to carry. Halving keeps
 * whole pixels, so an Atlas is still an Atlas at 42 across.
 *
 * `paint` is whether the screen's camo goes on. The library says no: a row is
 * there to be recognised, and a high-contrast scheme over a 84-pixel machine
 * makes every design in the list the same splatter. The roster says yes -
 * those are the player's machines, and the paint is what the strip is for.
 *
 * A plate where the release has no art for the design, which the index says
 * outright rather than leaving to a broken image.
 */
function sprite(
  unit: Unit,
  scale: 1 | 0.5,
  state: State,
  paint: boolean,
): HTMLElement {
  // The drawn size is a class rather than an inline style, so a narrow screen
  // can halve it in the stylesheet. An inline `width` beats a media query and
  // there is nowhere left to put the rule.
  const sized =
    scale === 1 ? "hangar-sprite" : "hangar-sprite hangar-sprite-half";
  if (!unit.sprite) {
    return el("span", { className: `${sized} hangar-sprite-none` });
  }

  const canvas = el("canvas", {
    className: sized,
    width: SPRITE_W,
    height: SPRITE_H,
  }) as HTMLCanvasElement;

  const url = unitSprite(unit.sprite);
  const camo = paint ? state.camo : null;
  const drawn = state.paint;
  void paintWhenReady(
    canvas,
    async () => {
      const art = await tile(url);
      return camo ? applyCamo(art, camo) : art;
    },
    () => drawn !== state.paint,
  ).catch((error: unknown) => {
    console.warn("hangar: a sprite could not be drawn", url, error);
  });
  return canvas;
}

/**
 * What kind of machine it is, in MegaMek's own words.
 *
 * `config` already carries both halves of the answer — the arrangement and
 * whether the design is an Omni — as "Biped", "Quad OmniMek", "LAM". A biped
 * is the arrangement nobody needs told about, so it drops to the unit type
 * and everything else keeps the word the release uses.
 */
function kind(unit: Unit): string {
  const rest = (unit.config ?? "").replace(/^Biped\s*/, "").trim();
  const type = unit.unit_type ?? "Mek";
  if (!rest) return type;
  return rest === "Quad" || rest === "Tripod" ? `${rest} ${type}` : rest;
}

/**
 * The chassis, with the design's other name after it.
 *
 * A Clan design has two names and MegaMek keeps them in separate fields, so a
 * row built from the chassis alone answers a search for `fire moth` with a
 * list of Dashers and nothing to say they are the same mech. Dimmed rather
 * than given its own line: it is one name, and the chassis is still the word
 * the eye scans the column for.
 */
function chassis(unit: Unit): (Node | string)[] {
  const other = secondName(unit);
  return other
    ? [
        unit.chassis,
        el("span", {
          className: "hangar-second-name",
          textContent: ` (${other})`,
        }),
      ]
    : [unit.chassis];
}

/**
 * One line of facts under a design's name, ending in what the machine is.
 *
 * An OmniMek says so here rather than in a mark beside the chassis. It is the
 * same class of fact as the role and the tech base — something true of the
 * design that a player either knows to look for or does not — and a badge in
 * the scan column claimed more of the eye than that. The word carries its own
 * explanation, because "OmniMek" tells somebody who has not met one nothing
 * at all.
 */
function facts(unit: Unit): HTMLElement {
  const said = kind(unit);
  const lead = [unit.role, unit.year, unit.tech_base].filter(Boolean);
  return el("div", { className: "hint hangar-hit-facts" }, [
    el("span", { textContent: `${lead.join(" · ")} · ` }),
    el("span", {
      className: "hangar-kind",
      textContent: said,
      ...(said.includes("Omni")
        ? { title: "An OmniMek: its weapon pods swap between configurations" }
        : {}),
    }),
  ]);
}

/**
 * One figure on the stat line: what it says, and what it means in full.
 *
 * Every one of them carries a `title`, because the line is abbreviations —
 * `5/8/0`, `21 hex` — and a player who has not met them yet needs somewhere
 * to find out without leaving the screen.
 */
function stat(
  figure: string,
  label: string,
  means: string,
  tone = "",
): HTMLElement {
  return el(
    "span",
    { className: `hangar-stat${tone ? ` ${tone}` : ""}`, title: means },
    [
      el("span", { className: "hangar-stat-n", textContent: figure }),
      el("span", { textContent: ` ${label}` }),
    ],
  );
}

/**
 * MegaMek's heat scale, at the two points on it a player can act on.
 *
 * At 5 a Mek is down a movement point, and at 14 it starts rolling not to
 * shut down — the release's own `HeatEffects` is where those two numbers come
 * from. Everything between them is one band, because the difference between
 * losing a second point of movement and taking another point of to-hit is not
 * a choice a force-building screen can help with; the difference between
 * "costs nothing", "costs something" and "might switch itself off" is.
 */
const HEAT_PENALTY = 5;
const HEAT_SHUTDOWN = 14;

/**
 * What one alpha strike does to a design's heat, and how much that costs.
 *
 * Signed the way a player asks the question: `+12 heat balance` is twelve
 * points the sinks cannot shed, `-4` is four points of room left over — enough
 * to fire everything and still run. A balance rather than a temperature: it is
 * what one alpha strike makes against what the sinks shed in the same turn,
 * the turn the firepower and the range beside it describe.
 *
 * Coloured at both ends and hardest at the top: warm and hot are a problem a
 * player has to plan around, and the cool tone on a negative is the absence of
 * one rather than a second warning. Nothing between nought and the first
 * threshold is coloured at all.
 *
 * Weapon heat alone. Moving adds to it and the index does not carry that, so
 * a design sitting just under the first threshold is over it the moment it
 * runs — which is what the wording says rather than implying a margin the
 * figure does not have.
 */
function heatStat(unit: Unit): HTMLElement[] {
  const excess = excessHeat(unit);
  if (excess === null) return [];

  const [tone, cost] =
    excess >= HEAT_SHUTDOWN
      ? ["hangar-stat-hot", "That is a shutdown roll before it has moved"]
      : excess >= HEAT_PENALTY
        ? [
            "hangar-stat-warm",
            "At 5 a Mek loses a movement point and at 8 it starts missing",
          ]
        : excess > 0
          ? ["", "Under the 5 where MegaMek's heat scale starts to bite"]
          : ["hangar-stat-cool", "It can fire everything and still move"];

  const made =
    excess > 0
      ? `Firing everything leaves ${excess} heat the sinks cannot shed in a turn.`
      : excess === 0
        ? "Firing everything makes exactly the heat the sinks shed in a turn."
        : `The sinks shed ${-excess} more heat in a turn than firing everything makes.`;

  return [
    stat(
      `${excess > 0 ? "+" : ""}${excess}`,
      "heat balance",
      `${made} ${cost}. Moving is not counted.`,
      tone,
    ),
  ];
}

/**
 * The overview line: what a design moves, wears and shoots.
 *
 * Every figure here is a column the index already carries, so this is the
 * whole of what the screen can say about a machine today — a loadout, armour
 * by location and quirks are in helm's database and are not published, which
 * is why the filter reference lists `equipment:` as coming soon.
 *
 * Firepower, reach and heat are helm's own descriptions rather than anything
 * MegaMek computes, so they are labelled as what they are rather than dressed
 * up as rules figures.
 *
 * Two lines, because they are two questions. What a machine moves, wears and
 * reaches is true of it whatever it does with its turn; what it lands and what
 * that costs it in heat is one alpha strike, and the two are read against each
 * other. Five figures on one line came to about 480px against a column 340
 * wide, so the split is what makes them fit as well as what makes them read -
 * and range sits with the machine rather than with the strike because the
 * three-and-two split is the one that fits once the figures are named
 * properly.
 *
 * A line with nothing on it is not drawn. Infantry carries no armour figure
 * and some entries carry no firepower, and an empty div is a blank row in a
 * column where every row is a line of text.
 */
function statLine(
  unit: Unit,
  curve: number[] | undefined,
  scale: number,
): HTMLElement[] {
  const carried = [
    stat(
      movement(unit),
      "MP",
      "Movement points: walking, running, jumping. A figure in brackets is the boosted run: MASC, a supercharger or triple-strength myomer",
    ),
    ...(unit.total_armor === null
      ? []
      : [
          stat(
            `${unit.total_armor}`,
            "armour",
            "Armour points over the whole machine",
          ),
        ]),
    ...(unit.max_range === null
      ? []
      : [
          stat(
            `${unit.max_range}`,
            "max range",
            "The longest range any weapon carried reaches, in hexes",
          ),
        ]),
  ];
  const fired = [
    ...(unit.firepower === null
      ? []
      : [
          stat(
            sayDamage(unit.firepower),
            "peak firepower",
            "The most this design can expect to land in a turn, at the range it is best at. Punches, kicks and hatchets are not counted",
          ),
        ]),
    ...heatStat(unit),
  ];
  return [
    ...[carried, fired]
      .filter((line) => line.length > 0)
      .map((line) => el("div", { className: "hint hangar-stats" }, line)),
    // The curve under the figures it came from. Too small for the ruler along
    // the bottom, and too small to repeat the damage figure standing above it,
    // so the one thing written on it is the hex it peaks at: the row says how
    // hard this design hits and the chart says where.
    //
    // Drawn to the scale of the whole result set rather than to its own peak,
    // so a row is as tall as the design hits: a Locust beside an Atlas is a
    // line near the axis, which is the true answer to "which of these two".
    ...(curve
      ? [
          el("div", { className: "hangar-curve", title: describe(curve) }, [
            chart({
              curve,
              max: scale,
              width: 150,
              height: 48,
              peak: "mark",
              hexes: false,
            }),
          ]),
        ]
      : []),
  ];
}

function targetField(
  label: string,
  value: number | null,
  suffix: string,
  onChange: (value: number | null) => void,
): HTMLElement {
  const input = el("input", {
    type: "number",
    className: "hangar-target-input",
    value: value === null ? "" : String(value),
    min: "0",
  }) as HTMLInputElement;
  input.addEventListener("change", () => {
    const parsed = Number.parseInt(input.value, 10);
    onChange(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
  });
  return el("label", { className: "hangar-target" }, [
    el("span", { className: "hangar-target-label", textContent: label }),
    input,
    el("span", { className: "hangar-target-suffix", textContent: suffix }),
  ]);
}

export function hangarScreen(): Node[] {
  const state: State = {
    units: [],
    curves: new Map(),
    picks: [],
    target: { ...NO_TARGET },
    query: "",
    shown: PAGE,
    sort: "name",
    nextId: 1,
    camo: null,
    paint: 0,
  };

  const strip = el("div", { className: "hangar-strip" });
  const totals = el("div", { className: "hangar-totals" });
  const warning = el("p", { className: "hangar-warning", hidden: true });
  const force = el("div", { className: "hangar-force-curve", hidden: true });
  const results = el("div", { className: "hangar-results" });
  const roster = el("div", { className: "hangar-roster" });
  const found = el("span", { className: "hint hangar-found" });

  /**
   * What order the results come back in.
   *
   * Beside the count rather than above the list, because it answers the same
   * question the count does — what am I looking at — and a filtered search
   * that came back alphabetically was eleven heavies a player had to read
   * every row of to compare.
   */
  const sorter = el(
    "select",
    { className: "hangar-sort", "aria-label": "Order the results" },
    ORDERS.map((order) =>
      el("option", { value: order.key, textContent: order.label }),
    ),
  ) as HTMLSelectElement;
  sorter.addEventListener("change", () => {
    state.sort = sorter.value;
    state.shown = PAGE;
    drawResults();
  });

  const searchBox = el("input", {
    type: "search",
    className: "hangar-search",
    placeholder: "Chassis, model, or a filter like bv<=1500",
    "aria-label": "Search the unit library",
  }) as HTMLInputElement;

  /** The filters in the box, as something to read and remove. */
  const pills = el("div", { className: "hangar-pills" });

  const closeHelp = el("button", {
    type: "button",
    className: "secondary small-btn",
    textContent: "Close",
  });

  const help = el("button", {
    type: "button",
    className: "secondary small-btn hangar-help-open",
    textContent: "Filters",
  });

  /**
   * What the box takes, and what it does not take yet.
   *
   * A dialog rather than a line of small print under the search: the list is
   * twenty keys long and the screen is a workbench, so it belongs where it
   * can be read and then put away. The unsupported half is in it on purpose —
   * a player who tries `equipment:"ER PPC"` and gets nothing deserves to know
   * whether they got the syntax wrong or asked for something the library does
   * not carry.
   */
  const syntaxDialog = el("dialog", { className: "hangar-help" }, [
    el("div", { className: "hangar-help-body" }, [
      el("h2", { textContent: "Searching the library" }),
      el("p", {
        textContent:
          "Bare words match a design's name. A term filters one column: a key, then one of : = < <= > >=, then a value. Terms combine with and.",
      }),
      el("ul", { className: "hangar-help-examples" }, [
        example(
          "hawk",
          "a word searches the name: every Phoenix Hawk, Black Hawk and Battle Hawk at once",
        ),
        example(
          'chassis:"black hawk"',
          "a key searches one column: the Black Hawks, and not one Phoenix Hawk",
        ),
        example(
          "class:heavy jump>0 year<=3025",
          "heavies that jump, introduced by 3025",
        ),
        example(
          "melee:yes clan:no",
          "a flag is asked with yes or no: Inner Sphere designs built to fight at arm's length",
        ),
        example(
          "omni:yes class:assault",
          "assault OmniMeks, whose pods swap between configurations",
        ),
      ]),
      el("h3", { textContent: "Filters" }),
      el("ul", { className: "hangar-help-filters" }, [
        filter(
          "name",
          "chassis and model together, the way a bare word reads them.",
          "name:atlas",
        ),
        filter(
          "chassis",
          "the chassis on its own, without the variant.",
          'chassis:"shadow hawk"',
        ),
        filter("model", "the variant code.", "model:as7-d"),
        filter(
          "class",
          "weight class by name: light, medium, heavy, assault.",
          "class:assault",
        ),
        filter("tech", "inner, clan or mixed.", "tech:clan"),
        filter(
          "role",
          "the role MegaMek assigns: brawler, striker, sniper, scout, skirmisher, juggernaut, missile boat.",
          "role:sniper",
        ),
        filter("type", "the kind of unit: mek, tank, infantry.", "type:mek"),
        filter("bv", "battle value.", "bv<=1500"),
        filter("cost", "the price in C-bills.", "cost<5000000"),
        filter("mass, tons, tonnage, weight", "tonnage.", "mass>=70"),
        filter("year", "the year the design was introduced.", "year<=3025"),
        filter(
          "armor, armour",
          "armour points over the whole machine.",
          "armor>=250",
        ),
        filter("walk", "walking movement points.", "walk>=5"),
        filter(
          "run",
          "running movement points, as the design has them rather than as walking rounds up.",
          "run>=8",
        ),
        filter("jump", "jumping movement points.", "jump>0"),
        filter(
          "damage, fp, firepower",
          "the most a design can expect to land in a turn, at the range it is best at. Physical attacks are not counted.",
          "damage>=40",
        ),
        filter(
          "range, reach",
          "the longest range any weapon carried reaches, in hexes.",
          "range>=18",
        ),
        filter(
          "heat",
          "heat one alpha strike leaves that the sinks cannot shed, so zero or less can keep firing.",
          "heat<=0",
        ),
      ]),
      el("h3", { textContent: "Booleans" }),
      el("p", {
        textContent:
          "These are asked with yes or no rather than compared with a number. A design the library does not answer for is returned by neither answer, so no is not everything that is not yes.",
      }),
      el(
        "ul",
        { className: "hangar-help-filters" },
        FLAG_KEYS.map((key) => flag(key, FLAG_MEANINGS[key])),
      ),
      el("h3", { textContent: "Not yet" }),
      el("ul", { className: "hangar-help-soon" }, [
        soon(
          "era",
          "era:jihad",
          "whether a design was available in a named era, which a year of introduction cannot answer",
        ),
        soon("equipment", 'equipment:"ER PPC"', "what a design carries"),
        soon("quirks", "quirks:battlefists", "MegaMek's per-design quirks"),
        soon("damage", "damage9>=20", "damage at a range band"),
        soon("engine", "engine:xl", "engine, structure and armour types"),
        soon("faction", "faction:steiner", "who fielded it, and when"),
      ]),
      el("div", { className: "row hangar-help-actions" }, [closeHelp]),
    ]),
  ]); /** What the target is imposing on the search, when it is locked. */
  const imposed = el("div", { className: "hangar-imposed" });

  /** Put a query back in the box and redraw from it. */
  function setQuery(text: string): void {
    state.query = text;
    searchBox.value = text;
    state.shown = PAGE;
    drawResults();
  }

  /**
   * The filter builder: a key, an operator and a value, appended to the box.
   *
   * The box stays the source of truth — this writes a term into it rather
   * than holding a filter of its own — so every filter is expressible as
   * text by construction, and the two halves cannot drift into disagreeing
   * about what is being searched.
   */
  const key = el(
    "select",
    { className: "hangar-pick", "aria-label": "Filter by" },
    FILTER_KEYS.map((name) => el("option", { value: name, textContent: name })),
  ) as HTMLSelectElement;
  const op = el("select", {
    className: "hangar-pick hangar-op",
    "aria-label": "How",
  }) as HTMLSelectElement;
  const value = el("input", {
    type: "text",
    className: "hangar-pick hangar-value",
    placeholder: "value",
    "aria-label": "Value",
  }) as HTMLInputElement;
  /**
   * The value control for a column that takes a closed set of values.
   *
   * Beside the box rather than instead of it: `chassis` takes anything a
   * player can type and `melee` takes two words, and one control cannot be
   * both. Whichever fits the chosen key is the one on screen.
   */
  const choice = el("select", {
    className: "hangar-pick hangar-value",
    "aria-label": "Value",
    hidden: true,
  }) as HTMLSelectElement;

  /**
   * Offer the operators and the values the chosen key actually takes.
   *
   * A flag compares with nothing — it is yes or no — and `melee>=3` is a term
   * a player can write and nothing can satisfy. So the operators are narrowed
   * to what the column answers to, and a closed set of values is a list to
   * choose from rather than a word to guess: not every player knows that a
   * flag is asked with `yes`, and an empty result does not teach them.
   */
  function dressBuilder(): void {
    const kind = filterKind(key.value);
    const ops =
      kind === "flag" ? [":"] : kind === "text" ? [":", "="] : ORDER_OPS;
    const held = op.value;
    op.replaceChildren(
      ...ops.map((symbol) =>
        el("option", { value: symbol, textContent: symbol }),
      ),
    );
    op.value = ops.includes(held) ? held : ops[0]!;
    op.disabled = ops.length === 1;

    const values = filterValues(key.value, state.units);
    choice.replaceChildren(
      ...values.map((one) => el("option", { value: one, textContent: one })),
    );
    choice.hidden = values.length === 0;
    value.hidden = values.length > 0;
  }
  key.addEventListener("change", dressBuilder);

  const addFilter = el("button", {
    type: "button",
    className: "secondary small-btn",
    textContent: "Add filter",
  });
  addFilter.addEventListener("click", () => {
    const typed = (choice.hidden ? value.value : choice.value).trim();
    if (!typed) return;
    const query = parseQuery(state.query);
    query.filters.push({ key: key.value, op: op.value as never, value: typed });
    setQuery(stringifyQuery(query));
    value.value = "";
  });

  const builder = el("div", { className: "hangar-builder" }, [
    key,
    op,
    value,
    choice,
    addFilter,
  ]);
  dressBuilder();

  function add(unit: Unit): void {
    state.picks.push({ unit, id: state.nextId++ });
    draw();
  }

  function remove(id: number): void {
    state.picks = state.picks.filter((pick) => pick.id !== id);
    draw();
  }

  function drawStrip(): void {
    const widths = shares(state.picks, state.target);
    const line = targetLine(state.picks, state.target);
    const cap = state.target.battleValue;

    strip.replaceChildren(
      ...state.picks.map((pick, at) => {
        const bv = pick.unit.bv ?? 0;
        const share = cap === null ? null : `${Math.round((bv / cap) * 100)}%`;
        const over = cap !== null && line !== null && sum(widths, at) > line;
        const segment = el(
          "div",
          {
            className: `hangar-seg${over ? " hangar-seg-over" : ""}`,
            title: `${pick.unit.name} — ${NUMBER.format(bv)} BV`,
          },
          [
            el("span", {
              className: "hangar-seg-name",
              textContent: pick.unit.chassis,
            }),
            // The model first, because it is the half of the name the strip
            // was missing: two Anvils of different variants drew as two bars
            // reading "ANVIL / Skirmisher · 60t", and a segment clips from
            // the right, so what a player needs to tell them apart has to be
            // on the end that survives.
            el("span", {
              className: "hangar-seg-facts",
              textContent: [
                pick.unit.model,
                pick.unit.role,
                `${pick.unit.mass ?? 0}t`,
              ]
                .filter(Boolean)
                .join(" · "),
            }),
            el("span", { className: "hangar-seg-bv" }, [
              el("strong", { textContent: NUMBER.format(bv) }),
              ...(share
                ? [
                    el("span", {
                      className: "hangar-seg-share",
                      textContent: share,
                    }),
                  ]
                : []),
            ]),
          ],
        );
        segment.style.width = `${widths[at]}%`;
        if (!over) {
          const tint = RAMP[Math.min(at, RAMP.length - 1)];
          segment.style.background = `color-mix(in oklab, var(--accent) ${tint}%, var(--bg))`;
        }
        return segment;
      }),
    );

    if (line !== null) {
      const mark = el("span", {
        className: "hangar-target-line",
        title: "Target",
      });
      mark.style.left = `${line}%`;
      strip.append(mark);
    }
  }

  function drawTotals(): void {
    const now = standing(state.picks, state.target);
    const cap = state.target.battleValue;

    totals.replaceChildren(
      el("span", {
        className: `hangar-total${now.overBy > 0 ? " over" : ""}`,
        textContent: NUMBER.format(now.battleValue),
      }),
      el("span", {
        className: "hint",
        textContent: cap === null ? "BV" : `/ ${NUMBER.format(cap)} BV`,
      }),
      el("span", {
        className: "hint hangar-standing",
        textContent:
          cap === null
            ? `${now.units} units · ${now.tonnage} tons`
            : now.overBy > 0
              ? `${NUMBER.format(now.overBy)} over target`
              : `${NUMBER.format(now.remaining ?? 0)} remaining`,
      }),
    );

    const notes: string[] = [];
    if (now.overBy > 0) {
      notes.push(
        `${NUMBER.format(now.battleValue)} BV against a target of ${NUMBER.format(cap ?? 0)}. The force is still playable; an event that enforces the target will reject it.`,
      );
    }
    if (now.tooLate.length) {
      const names = now.tooLate.map((p) => p.unit.name).join(", ");
      notes.push(`Later than the year target: ${names}.`);
    }
    warning.textContent = notes.join(" ");
    warning.hidden = notes.length === 0;
  }

  /**
   * What the force covers, hex by hex.
   *
   * The one chart on this screen that is worth more than the sum of its rows:
   * four designs that peak in four different places cover a board between
   * them, and four that peak in the same place leave a hole where the fifth
   * should be. Each machine is drawn faintly underneath with its own best
   * marked, so where the hole is is visible rather than inferred.
   *
   * Absent rather than empty when there is nothing to say: no picks, no
   * curves for the picks, or a force that lands nothing at any range.
   */
  function drawForceCurve(): void {
    const carried = state.picks
      .map((pick) => state.curves.get(pick.unit))
      .filter((curve): curve is number[] => curve !== undefined);
    const total = totalCurve(carried);
    const runs = bestHexes(total);
    if (!runs.length) {
      force.hidden = true;
      force.replaceChildren();
      return;
    }

    const most = Math.max(...total);
    const [first] = runs;
    const one = runs.length === 1 && first?.[0] === first?.[1];
    force.hidden = false;
    force.replaceChildren(
      el("div", { className: "hangar-force-curve-head" }, [
        el("span", {
          className: "hint",
          textContent: `Firepower · ${carried.length} of ${state.picks.length} designs`,
        }),
        el("span", {
          className: "hangar-force-peak",
          textContent: `${sayDamage(most)} at ${one ? "hex" : "hexes"} ${sayHexes(runs)}`,
        }),
      ]),
      chart({
        curve: total,
        width: 520,
        height: 132,
        faint: carried,
        of: "The force",
      }),
    );
  }

  function drawRoster(): void {
    if (!state.picks.length) {
      roster.replaceChildren(
        el("p", {
          className: "hint",
          textContent:
            "Nothing in the force yet. Search the library to add a design.",
        }),
      );
      return;
    }

    roster.replaceChildren(
      el(
        "ul",
        { className: "hangar-row-list" },
        state.picks.map((pick) => {
          const drop = el("button", {
            type: "button",
            className: "secondary small-btn",
            textContent: "Remove",
            "aria-label": `Remove ${pick.unit.name}`,
          });
          drop.addEventListener("click", () => {
            remove(pick.id);
          });
          return el("li", { className: "hangar-row" }, [
            sprite(pick.unit, 0.5, state, true),
            el("div", { className: "hangar-row-name" }, [
              el("div", { className: "hangar-row-title" }, [
                el("div", { className: "hangar-chassis" }, chassis(pick.unit)),
                el("div", {
                  className: "hint hangar-model",
                  textContent: pick.unit.model ?? "",
                }),
              ]),
              el("div", {
                className: "hint hangar-row-facts",
                textContent: `${pick.unit.mass ?? 0}t · ${movement(pick.unit)} · G4 P5 · ${NUMBER.format(pick.unit.bv ?? 0)} BV`,
              }),
            ]),
            drop,
          ]);
        }),
      ),
    );
  }

  function drawResults(): void {
    const query = state.query.trim();
    const now = standing(state.picks, state.target);

    // Nothing typed is not nothing to show: an empty list under an empty box
    // reads as a screen still loading, and says nothing about what is in the
    // library. A spread across the weight classes does both.
    // Every match, not a capped sample: filtering 4,279 rows costs a
    // fraction of a millisecond, and a count that says "400+" when the answer
    // is 38 is worse than no count.
    // The target's own terms go in front of what was typed, so a locked
    // target and a typed filter are one query rather than two mechanisms
    // arguing. Battle value stays out of it and is applied below: what fits
    // depends on the force, which no static term can say.
    const terms = targetTerms(state.target);
    const locked = terms.join(" ");
    const full = [locked, query].filter(Boolean).join(" ");

    // The typed filters, as pills that can be taken off. Rendered from the
    // query rather than from state of their own: the box is what is being
    // searched, and a pill is a view of one term in it.
    const typed = parseQuery(state.query);
    pills.replaceChildren(
      ...typed.filters.map((filter, at) => {
        const drop = el("button", {
          type: "button",
          className: "hangar-pill-drop",
          textContent: "\u00d7",
          "aria-label": `Remove filter ${filter.key}${filter.op}${filter.value}`,
        });
        drop.addEventListener("click", () => {
          typed.filters.splice(at, 1);
          setQuery(stringifyQuery(typed));
        });
        return el("span", { className: "hangar-pill" }, [
          el("code", {
            textContent: `${filter.key}${filter.op}${filter.value}`,
          }),
          drop,
        ]);
      }),
    );

    // Shown rather than silent: a search answering with a third of the library
    // and no reason given is a screen a player argues with.
    imposed.replaceChildren(
      ...(state.target.mode === "locked" &&
      (terms.length ||
        state.target.battleValue !== null ||
        state.target.tonnage !== null)
        ? [
            el("span", { className: "hint", textContent: "Locked by target:" }),
            ...terms.map((term) =>
              el("code", { className: "hangar-term", textContent: term }),
            ),
            ...(state.target.battleValue !== null
              ? [
                  el("code", {
                    className: "hangar-term",
                    textContent: `bv<=${NUMBER.format(Math.max(0, now.remaining ?? 0))}`,
                  }),
                ]
              : []),
            ...(state.target.tonnage !== null
              ? [
                  el("code", {
                    className: "hangar-term",
                    textContent: `tons<=${Math.max(0, state.target.tonnage - now.tonnage)}`,
                  }),
                ]
              : []),
          ]
        : []),
    );

    const matches = full
      ? search(state.units, full, state.units.length).filter((unit) =>
          withinTarget(unit, state.target, now),
        )
      : suggestions(state.units).filter((unit) =>
          withinTarget(unit, state.target, now),
        );

    const hits = ordered(matches, state.sort).slice(0, state.shown);
    // Every design the query matched, not only the page of them on screen:
    // "Show more" must not rescale the rows already read, and a filter is a
    // question about a set. Recomputed each render because the set is what
    // changes.
    const scale = scaleAcross(
      matches
        .map((unit) => state.curves.get(unit))
        .filter((curve): curve is number[] => curve !== undefined),
    );
    found.textContent =
      query || locked
        ? `${NUMBER.format(matches.length)} matching`
        : `${NUMBER.format(state.units.length)} designs`;

    if (!hits.length) {
      results.replaceChildren(
        el("p", {
          className: "hint",
          textContent: state.units.length
            ? "No design matches that."
            : "Loading the unit library…",
        }),
      );
      return;
    }

    // A list, because that is what it is: a screen reader is told how many
    // designs came back and where each one starts, which a run of divs cannot
    // say. The "Show more" button is a sibling of the list rather than an item
    // in it — it is not one of the results.
    const list = el(
      "ul",
      { className: "hangar-hit-list" },
      hits.map((unit) => {
        const over = now.remaining !== null && (unit.bv ?? 0) > now.remaining;
        // A plus rather than the word. The row is five columns wide and the
        // stat lines wanted every pixel of the name column; the label was the
        // one thing on the row a player never has to read twice. The name is
        // still in the button, on the label a screen reader announces.
        const button = el("button", {
          type: "button",
          className: "small-btn hangar-add",
          textContent: "+",
          "aria-label": `Add ${unit.name}`,
          title: `Add ${unit.name}`,
        });
        button.addEventListener("click", () => {
          add(unit);
        });
        return el("li", { className: "hangar-hit" }, [
          sprite(unit, 1, state, false),
          el("div", { className: "hangar-hit-name" }, [
            el("div", { className: "hangar-chassis" }, chassis(unit)),
            el("div", {
              className: "hint hangar-model",
              textContent: unit.model ?? "",
            }),
            facts(unit),
            ...statLine(unit, state.curves.get(unit), scale),
          ]),
          el("span", {
            className: "hangar-hit-mass",
            textContent: `${unit.mass ?? 0} t`,
          }),
          el("span", { className: "hangar-hit-bv" }, [
            el("strong", { textContent: NUMBER.format(unit.bv ?? 0) }),
            ...(over
              ? [
                  el("span", {
                    className: "hangar-over",
                    textContent: `+${NUMBER.format((unit.bv ?? 0) - (now.remaining ?? 0))} over`,
                  }),
                ]
              : []),
          ]),
          button,
        ]);
      }),
    );

    results.replaceChildren(
      ...(query || locked
        ? []
        : [
            el("p", {
              className: "hint hangar-suggested",
              textContent: "A few from each weight class — type to search.",
            }),
          ]),
      list,
    );

    // The rest of them, a page at a time. Without this the count said 38 and
    // the list showed 12, with nothing to say where the other 26 went.
    if (matches.length > hits.length) {
      const more = el("button", {
        type: "button",
        className: "secondary hangar-more",
        textContent: `Show ${Math.min(PAGE, matches.length - hits.length)} more of ${matches.length}`,
      });
      more.addEventListener("click", () => {
        state.shown += PAGE;
        drawResults();
      });
      results.append(more);
    }
  }

  function draw(): void {
    drawStrip();
    drawTotals();
    drawForceCurve();
    drawRoster();
    drawResults();
  }

  searchBox.addEventListener("input", () => {
    state.query = searchBox.value;
    state.shown = PAGE;
    drawResults();
  });

  const targets = el("div", { className: "hangar-targets" }, [
    el("span", { className: "hangar-targets-label" }, [
      el("strong", { textContent: "Force target" }),
      el("span", { className: "hint", textContent: " optional" }),
    ]),
    targetField("Battle value", null, "BV", (value) => {
      state.target.battleValue = value;
      draw();
    }),
    targetField("Year", null, "or earlier", (value) => {
      state.target.year = value;
      draw();
    }),
    targetField("Tonnage", null, "tons", (value) => {
      state.target.tonnage = value;
      draw();
    }),
  ]);

  const lock = el("label", { className: "hangar-lock" }, [
    el("input", { type: "checkbox", className: "hangar-lock-box" }),
    el("span", {
      textContent: "Filter the library to the target",
    }),
  ]);
  lock.querySelector("input")!.addEventListener("change", (event) => {
    state.target.mode = (event.target as HTMLInputElement).checked
      ? "locked"
      : "advisory";
    drawResults();
  });
  targets.append(lock);

  // The paint the force fights in. Its own block rather than a field on the
  // force, because a camo is a record in the player's repository and the force
  // is not saved anywhere yet: what this holds is a choice, and the force will
  // carry a reference to it once `blue.lance.force` exists.
  //
  // Filled in when a session turns up, which is the one thing on this screen
  // that needs one. A signed-out reader still gets everything else, so the
  // read is a side errand rather than a gate on the route.
  //
  // Signed out it still offers None and a roll — a camo made in the browser
  // and kept nowhere, which needs no account — and says what an account would
  // add. A dead prompt where a control belongs is worse than either.
  /**
   * Follow a picker's selection with the paint on the screen.
   *
   * The strip is the only place camo is chosen, and every sprite in the force
   * is painted from that one choice — so this is the whole of "selecting a
   * camo repaints the hangar". A redraw is a re-composite of sprites already
   * decoded, which is why it can be done on every selection rather than
   * debounced.
   */
  function follow(picker: Picker): Picker {
    picker.onChange = () => {
      void pickedPixels(picker)
        .then((pixels) => {
          state.camo = pixels;
          state.paint += 1;
          draw();
        })
        .catch((error: unknown) => {
          console.warn("hangar: the camo could not be read", error);
        });
    };
    return picker;
  }

  /**
   * The strip, once the session read has answered.
   *
   * One picker, not two. A picker built before the answer has to be a
   * signed-out one, which rolls a camo and repaints every sprite on the
   * screen — so a player with a default of their own watched the hangar open
   * in a random paint and then change to theirs a moment later. The read is a
   * promise the masthead has already started, so what this waits on is
   * usually already in flight.
   */
  function mountCamo(session: Session | null): void {
    const picker = follow(camoPicker(session, "random"));
    const opened = camoModal(picker, "Choose camo", "swatch");
    camo.replaceChildren(opened.button, opened.dialog);
  }

  /**
   * The camo choice, as a square in the force's own header.
   *
   * A card of its own under the roster was a strip of tiles that grew with
   * the collection and said what it was about only by sitting near it. The
   * choice is about these machines, painted in this - so it is a swatch of
   * what they are wearing, in the header of the card that lists them, and the
   * strip itself is the dialog behind it. The same shape the lobby already
   * uses, for the same reason.
   *
   * Empty until the session read answers - see `mountCamo` - and the same
   * size either way, so the header does not jump when the swatch arrives.
   */
  const camo = el("span", { className: "hangar-camo" });

  const screen = el("div", { className: "hangar" }, [
    targets,
    el("div", { className: "hangar-meter" }, [totals, strip, warning, force]),
    el("div", { className: "hangar-panes" }, [
      el("section", { className: "card hangar-library" }, [
        el("div", { className: "hangar-search-row" }, [
          searchBox,
          help,
          sorter,
          found,
        ]),
        pills,
        builder,
        imposed,
        results,
        syntaxDialog,
        el("div", { className: "hangar-ways-in" }, [
          el(
            "a",
            {
              className: "hangar-mekbay",
              href: "https://mekbay.com/",
              rel: "noreferrer",
              target: "_blank",
            },
            [document.createTextNode("Build a force on MekBay")],
          ),
          el("span", {
            className: "hint",
            textContent: "Importing a .mul is not built yet.",
          }),
        ]),
      ]),
      el("div", { className: "hangar-side" }, [
        el("section", { className: "card hangar-force" }, [
          el("div", { className: "hangar-force-head" }, [
            el("h2", { textContent: "The force" }),
            camo,
          ]),
          roster,
          el("p", {
            className: "hint hangar-unsaved",
            textContent:
              "Nothing here is saved yet — the force lasts as long as this page does. Saving one, and exporting it as a .mul, will need an account.",
          }),
        ]),
      ]),
    ]),
  ]);

  help.addEventListener("click", () => {
    syntaxDialog.showModal();
  });
  closeHelp.addEventListener("click", () => {
    syntaxDialog.close();
  });

  draw();

  void currentSession()
    .then(mountCamo)
    .catch((error: unknown) => {
      // The API being down costs the camo strip and nothing else: the library
      // is a published file and the force is arithmetic over it. The strip
      // still goes up, as the signed-out one: no saved camo to read, but None
      // and a roll need no account and no API.
      console.warn("hangar: the session could not be read", error);
      mountCamo(null);
    });

  // Which library, asked of the API rather than compiled in, so a helm
  // deploy reaches this screen without a site build.
  void library()
    .then(async ({ helm, megamek }) => {
      const url = unitIndexUrl(helm, megamek);
      return { url, index: await loadUnits(url) };
    })
    .then(
      ({ url, index }) => {
        state.units = index.units;
        // The roles and tech bases the picker offers are read off the index,
        // so they cannot be offered until it is here.
        dressBuilder();
        draw();
        // After the screen is drawn rather than before it. Nothing on the
        // screen waits for the curves: the library is searchable the moment
        // the index lands, and the charts appear on the next draw.
        void loadCurves(url, index).then((curves) => {
          if (!curves.size) return;
          state.curves = curves;
          draw();
        });
      },
      (error: unknown) => {
        console.warn("hangar: could not load the unit index", error);
        results.replaceChildren(
          el("p", {
            className: "error",
            textContent:
              "The unit library could not be loaded, so there is nothing to search.",
          }),
        );
      },
    );

  return [
    // No page head. The masthead highlights Hangar and the URL says it, so a
    // heading that repeats the tab and a line describing the screen to
    // somebody already looking at it are both just space above the work.
    screen,
    footer(),
  ];
}

/** The widths up to and including `at`, for placing the target line. */
function sum(widths: number[], at: number): number {
  return widths.slice(0, at + 1).reduce((total, width) => total + width, 0);
}

/** Re-render into the page, for the router. */
export function showHangar(): void {
  render(...hangarScreen());
}
